import {computePosition, autoUpdate, offset, shift, arrow, flip, type Placement, hide} from '@floating-ui/dom'
import {type TranslationArrayKeys, type TranslationObject} from '@colonist/i18n'
import {
    createDivIn,
    createHeaderIn,
    createListItemIn,
    createUnorderedListIn,
    setElementInnerHTML,
    translate,
} from './DomUtils.ts'
import {clientI18n} from '../ClientI18n.ts'
import {UIDeviceController} from '../ui_html/UIDeviceController.ts'

type TooltipPlacementType = 'top' | 'top-start' | 'top-end' | 'bottom' | 'bottom-start' | 'bottom-end' | 'right' | 'right-start' | 'right-end' | 'left' | 'left-start' | 'left-end'

type TooltipWidthType = 'small' | 'medium' | 'large'

type TooltipFontSizeType = 'small' | 'medium'

export interface TooltipListContent {
    listTitle: TranslationObject
    list: TranslationArrayKeys
}

let autoUpdateCleanupCallback: (() => void) | null = null
let tooltip: HTMLElement | null = null
let tooltipArrow: HTMLElement | null = null
let currentHoveredElement: HTMLElement | null = null
let currentContent: HTMLElement | null = null
let currentPosition: TooltipPlacementType = 'bottom'

const zoomContainerSelectors = ['.scene_room_left', '.scene_room_mid', '.scene_room_right']
const zoomContainers: HTMLElement[] = zoomContainerSelectors
    .map((selector) => document.querySelector(selector))
    .filter(Boolean) as HTMLElement[]
const isAffectedByZoomBug = UIDeviceController.isUserOnSafariOrIOS()

export function initTooltips() {
    createTooltip()
    attachElementListenersToEjsTooltips()
}

export function createTooltipIn(element: HTMLElement, tooltipText: TranslationObject, position?: TooltipPlacementType, fontSize?: TooltipFontSizeType, width?: TooltipWidthType) {
    const isTextAlreadyTranslated = tooltipText.key === 'strings:colonistCommon.utils.#buffer'
    const text = isTextAlreadyTranslated ? tooltipText.options?.value : translate(tooltipText.key, tooltipText.options)
    element.setAttribute('data-tooltip', text)
    if(position != undefined) element.setAttribute('data-tooltip-position', position)
    if(width != undefined) element.setAttribute('data-tooltip-width', width)
    if(fontSize != undefined) element.setAttribute('data-tooltip-fontsize', fontSize)

    attachElementListeners(element)
}

export function updateTooltipText(element: HTMLElement, tooltipText: TranslationObject) {
    const isTextAlreadyTranslated = tooltipText.key === 'strings:colonistCommon.utils.#buffer'
    const text = isTextAlreadyTranslated ? tooltipText.options?.value : translate(tooltipText.key, tooltipText.options)
    element.setAttribute('data-tooltip', text)
}

export function createListTooltipIn(element: HTMLElement, content: TooltipListContent, position?: TooltipPlacementType, fontSize?: TooltipFontSizeType, width?: TooltipWidthType) {
    const contentParsed = {
        listTitle: translate(content.listTitle.key, content.listTitle.options),
        list: clientI18n.tArray(content.list),
    }
    element.setAttribute('data-tooltip-list', JSON.stringify(contentParsed))
    if(position != undefined) element.setAttribute('data-tooltip-position', position)
    if(width != undefined) element.setAttribute('data-tooltip-width', width)
    if(fontSize != undefined) element.setAttribute('data-tooltip-fontsize', fontSize)

    attachElementListeners(element)
}

export function disableTooltip(element: HTMLElement) {
    element.setAttribute('data-tooltip-hidden', 'true')
}

export function enableTooltip(element: HTMLElement) {
    element.removeAttribute('data-tooltip-hidden')
}

export function hideTooltip() {
    if(tooltip == undefined) return

    tooltip.classList.remove('visible')
    currentHoveredElement = null
    if(autoUpdateCleanupCallback) {
        autoUpdateCleanupCallback()
        autoUpdateCleanupCallback = null
    }
}

function attachElementListenersToEjsTooltips() {
    document.querySelectorAll('[data-tooltip]').forEach((element: HTMLElement) => {
        attachElementListeners(element)
    })
}

function attachElementListeners(element: HTMLElement) {
    if(element.hasAttribute('data-tooltip-listeners')) return

    element.addEventListener('mouseover', (e) => {
        if(element.getAttribute('data-tooltip-hidden') === 'true') return
        showTooltip(element)
    })

    element.addEventListener('mouseout', (e) => {
        hideTooltip()
    })

    element.setAttribute('data-tooltip-listeners', 'true')
}

function createTooltip() {
    const existingTooltip = document.getElementById('tooltip')
    if(existingTooltip != null) return

    tooltip = document.createElement('div')
    tooltip.className = 'floating-tooltip'

    tooltipArrow = createDivIn(tooltip, 'floating-tooltip-arrow')

    currentContent = createDivIn(tooltip, 'tooltip-content')

    document.body.appendChild(tooltip)
}

function showTooltip(hoveredElement: HTMLElement) {
    if(tooltip == undefined) return
    if(currentContent == undefined) return
    if(!shouldShowTooltip(hoveredElement)) return

    currentHoveredElement = hoveredElement
    currentPosition = getTooltipPlacement(hoveredElement)

    setElementInnerHTML(currentContent, {key: 'strings:colonistCommon.utils.emptyString'})

    const listContent = hoveredElement.getAttribute('data-tooltip-list')
    if(listContent) {
        try {
            const content = JSON.parse(listContent) as any
            createListContent(content)
        } catch(e) {
            currentContent.innerText = 'Error displaying tooltip'
        }
    } else {
        const tooltipText = hoveredElement.getAttribute('data-tooltip')
        if(tooltipText == undefined) return
        currentContent.innerText = tooltipText
    }

    tooltip.style.maxWidth = getTooltipWidth(hoveredElement)
    tooltip.style.padding = getTooltipPadding(hoveredElement)
    currentContent.style.fontSize = getTooltipFontSize(hoveredElement)

    tooltip.classList.add('visible')
    void updatePosition()
}

function shouldShowTooltip(element: HTMLElement): boolean {
    if(!isAffectedByZoomBug) return true

    const isZoomed = zoomContainers.some((container) => {
        const zoomValue = window.getComputedStyle(container).zoom
        return zoomValue !== '1'
    })
    if(!isZoomed) return true

    return !zoomContainers.some((container) => container.contains(element))
}

async function updatePosition() {
    if(tooltip == undefined) return
    if(currentHoveredElement == undefined) return

    if(autoUpdateCleanupCallback) {
        autoUpdateCleanupCallback()
    }

    autoUpdateCleanupCallback = autoUpdate(currentHoveredElement,
        tooltip,
        async() => {
            try {
                if(tooltip == undefined) return
                if(tooltipArrow == undefined) return
                if(currentHoveredElement == undefined) return

                const tooltipPadding = 8

                const positionOptions = {
                    placement: currentPosition as Placement,
                    middleware: [
                        offset(tooltipPadding),
                        flip({padding: tooltipPadding}),
                        shift({padding: tooltipPadding}),
                        arrow({element: tooltipArrow}),
                        hide(),
                    ],
                }
                const computeResult = await computePosition(currentHoveredElement, tooltip, positionOptions)

                const {x, y, placement, middlewareData} = computeResult

                if(middlewareData.hide?.referenceHidden) {
                    hideTooltip()
                    return
                }

                const moveTooltip = () => {
                    if(tooltip == undefined) return
                    Object.assign(tooltip.style, {
                        left: `${x}px`,
                        top: `${y}px`,
                    })
                }
                moveTooltip()

                if(middlewareData.arrow == undefined) return

                const {x: arrowX, y: arrowY} = middlewareData.arrow

                const arrowSide = {
                    top: 'bottom',
                    right: 'left',
                    bottom: 'top',
                    left: 'right',
                }[placement.split('-')[0]]

                if(arrowSide == undefined) return
                const moveArrow = () => {
                    if(tooltipArrow == undefined) return
                    Object.assign(tooltipArrow.style, {
                        left: arrowX != null ? `${arrowX}px` : '',
                        top: arrowY != null ? `${arrowY}px` : '',
                        right: '',
                        bottom: '',
                        [arrowSide]: '-4px',
                    })
                }
                moveArrow()
            } catch(error) {
                return
            }
        })
}

function createListContent(content: any) {
    if(currentContent == undefined) return

    createHeaderIn(currentContent, {key: 'strings:colonistCommon.utils.#buffer', options: {value: content.listTitle}}, 'tooltip-list-title', undefined, 'H4')

    const listContent = createUnorderedListIn(currentContent, 'tooltip-list')
    content.list.forEach((item: string) => {
        createListItemIn(listContent, {key: 'strings:colonistCommon.utils.#buffer', options: {value: item}}, '')
    })
}

function getTooltipPlacement(element: HTMLElement): TooltipPlacementType {
    const position = element.getAttribute('data-tooltip-position')
    const validPositions: TooltipPlacementType[] = [
        'top', 'top-start', 'top-end',
        'bottom', 'bottom-start', 'bottom-end',
        'right', 'right-start', 'right-end',
        'left', 'left-start', 'left-end',
    ]

    if(!validPositions.includes(position as TooltipPlacementType)) return 'bottom'
    return position as TooltipPlacementType
}

function getTooltipWidth(element: HTMLElement): string {
    const width = element.getAttribute('data-tooltip-width')
    const validWidths: TooltipWidthType[] = ['small', 'medium', 'large']
    const widthEnum = validWidths.includes(width as TooltipWidthType)
        ? width as TooltipWidthType
        : 'medium'

    switch(widthEnum) {
        case 'small': return '112px'
        case 'medium': return '224px'
        case 'large': return '304px'
    }
}

function getTooltipFontSize(element: HTMLElement): string {
    const fontSizeEnum = getTooltipFontSizeType(element)

    switch(fontSizeEnum) {
        case 'small': return '10px'
        case 'medium': return '12px'
    }
}

function getTooltipPadding(element: HTMLElement): string {
    const fontSizeEnum = getTooltipFontSizeType(element)
    switch(fontSizeEnum) {
        case 'small': return '4px'
        case 'medium': return '8px'
    }
}

function getTooltipFontSizeType(element: HTMLElement): TooltipFontSizeType {
    const fontType = element.getAttribute('data-tooltip-fontsize')
    const validFontSizeTypes: TooltipFontSizeType[] = ['small', 'medium']
    const fontSizeTypeEnum = validFontSizeTypes.includes(fontType as TooltipFontSizeType)
        ? fontType as TooltipFontSizeType
        : 'medium'
    return fontSizeTypeEnum
}
